/**
 * TODO: @cdonohue PageState and friends from js/designer-flux are needed here to have proper typing.
 * This will involve dealing with some Immutable Record flowtypes and also
 * unwinding a bit of one-off types that have been created in individual files.
 * Leaving this comment and a few FixMeAny types in here until we have those types
 * behind a boundary that we can import from
 */

/**
 * For querying the PageManager's state and deriving any data that is highly
 * specific to the PageManager's UI components.
 */

import {partition} from 'lodash';
import {List, type List as ListType, type Map as ImmutableMap} from 'immutable';
import {Map as immMap} from 'immutable';
import {isNil} from '@packages/utilities/fp/utils';
import {
  getDynPages,
  getNodeTreeDepth,
  getPageNodes,
  getPageTreeNodes,
  getRootNode,
  ROOT_NODE,
  isRoot,
  nodeAncestry,
  nodesWithParentId,
  upsertNode,
} from './PageTreeUtils';
import {
  isDynPage,
  isPage,
  isStandaloneBranchParentPage,
  isStaticTemplatePage,
  isUtilityPage,
  mintPage,
  isStandaloneBranchPage,
  isBranchPage,
  isDraftPage,
} from './PageUtils';
import {mintFolder} from './FolderUtils';
import {get} from '@packages/utilities/data-utils';
import PageNode, {
  PageListNode,
  PageListNodeType,
  type PageRecordType,
  type PageState,
} from '@packages/systems/core/records/PageRecord';
import {isMint} from '@packages/systems/core/utils/RecordUtils';
import {PageManagerUIState} from '@packages/systems/designer/page';
import {allowedStaticUsysPages} from './PageLinkUtils';
import type {CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import type {Locale} from '@packages/systems/localization-types';
import {COMMERCE_PLUGIN_KEY} from '@packages/systems/commerce/constants';

const getSelectedItem = get('selectedItem');

/**
 * Retrieves the currently selected item without any unpersisted modifications.
 * If the currently selected item is not persisted, returns an empty Map.
 *
 * @param  {PageState} pageState
 * @param  {PageManagerState} pageManagerState
 * @return {PageNode|Map}
 */
export const selectionSnapshot = (
  pageState: PageState,
  pageManagerState: PageManagerUIState,
  currentLocale: Locale | null | undefined,
  canDraftPagesPerLocale: boolean
): PageRecordType => {
  const pageNodes = getPageNodes(pageState);
  const selectedItem = get('selectedItem', pageManagerState);

  if (pageNodes.has(selectedItem.id)) {
    return get(selectedItem.id, pageNodes);
  } else if (isMint(selectedItem)) {
    return mintNodeSnapshot(
      selectedItem,
      pageNodes,
      currentLocale,
      canDraftPagesPerLocale
    );
  }

  return PageNode();
};

/**
 * Recreates the initial state of the provided mint `node`. This is useful for
 * determining if a mint item has been modified.
 *
 * @param  {PageRecordType & {parentId: string}} node
 * @param  {Immutable.Map.<String, PageRecordType>} pageNodes
 * @return {PageRecordType}
 */
const mintNodeSnapshot = (
  node: PageRecordType & {parentId: string},
  pageNodes: ImmutableMap<string, PageRecordType>,
  currentLocale: Locale | null | undefined,
  canDraftPagesPerLocale: boolean
): PageRecordType => {
  if (
    isStandaloneBranchParentPage(node, {currentLocale, canDraftPagesPerLocale})
  ) {
    return mintPage(node.parentId, pageNodes).merge({
      id: node.id,
      isStandaloneBranch: true,
      draft: true,
    });
  } else if (isStaticTemplatePage(node)) {
    return mintPage(node.parentId, pageNodes).merge({
      id: node.id,
      isStaticTemplate: true,
      draft: true,
      slug: node.slug,
    });
  } else if (isPage(node)) {
    return mintPage(node.parentId, pageNodes).set('id', node.id);
  }
  return mintFolder(pageNodes).set('id', node.id);
};
/**
 * Retrieves the currently selected item with unpersisted modifications applied.
 *
 * @param  {PageManagerState} pageManagerState
 * @return {Page|Folder}
 */
export const selection = (pageManagerState: PageManagerUIState) =>
  get('selectedItem', pageManagerState);

/**
 * Constructs a list containing one Boolean value for each ancestor of the node
 * with the provided id. Each boolean represents whether or not the ancestor
 * in that position is in the tail position within its list of siblings. This is
 * used to derive the state of the visual branching in the PageList.
 *
 * @param  {String} id
 * @param  {Immutable.Map.<String, PageNode>} pageTreeNodes
 * @return {List.<Boolean>}
 */
export const branches = (
  id: string,
  pageTreeNodes: ImmutableMap<string, PageRecordType>
) =>
  nodeAncestry(id, pageTreeNodes)
    .toSeq()
    .butLast()
    .reverse()
    // @ts-expect-error - TS7006 - Parameter 'ancestor' implicitly has an 'any' type. | TS2345 - Argument of type 'Immutable.Map<string, import("/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/packages/systems/core/records/PageRecord/index").PageRecordType>' is not assignable to parameter of type 'Map<string, import("/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/packages/systems/core/records/PageRecord/index").PageRecordType>'.
    .map((ancestor) => isLastSibling(ancestor, pageTreeNodes))
    .toList();

/**
 * Returns a modified version of the PageStore's contents with any ephemeral
 * state held by the PageManager merged in.
 *
 * @param  {PageStore} pageState
 * @param  {PageManagerUIStore} pageManagerState
 * @return {PageStore}
 */
export const getCurrentPageState = (
  pageState: PageState,
  pageManagerState: PageManagerUIState
) => {
  if (!hasSelectedItem(pageManagerState)) {
    return pageState;
  }

  const subTreeToUpdate = isDynPage(selection(pageManagerState))
    ? 'dynamicPages'
    : 'staticPages';

  return pageState.update(
    subTreeToUpdate,
    upsertNode(selection(pageManagerState))
  );
};

/**
 * Builds a map from parent IDs to their children in O(n) time.
 * This allows O(1) lookup of children instead of O(n) filtering.
 */
export const buildParentToChildrenMap = (
  pageTreeNodes: ImmutableMap<string, PageRecordType>
): Map<string, PageRecordType[]> => {
  const map = new Map<string, PageRecordType[]>();

  pageTreeNodes.valueSeq().forEach((node) => {
    // Skip ROOT_NODE to prevent it from becoming its own child, otherwise
    // we might encounter a circular reference down the line.
    if (node.id === ROOT_NODE.id) {
      return;
    }

    const parentId = node.parentId || ROOT_NODE.id;
    const children = map.get(parentId);
    if (children) {
      children.push(node);
    } else {
      map.set(parentId, [node]);
    }
  });

  // Sort children by siblingIndex for each parent
  map.forEach((children) => {
    children.sort((a, b) => (a.siblingIndex || 0) - (b.siblingIndex || 0));
  });

  return map;
};

/**
 * Counts all page descendants of a node using pre-computed parent-to-children map.
 *
 * `isCountable` lets callers exclude pages the list never renders — without it
 * a collapsed folder reports more pages than expanding it reveals.
 */
export const countDescendants = (
  id: string,
  parentToChildrenMap: Map<string, PageRecordType[]>,
  isCountable: (page: PageRecordType) => boolean = () => true
): number => {
  const children = parentToChildrenMap.get(id) || [];

  return children.reduce((count, child) => {
    const childContribution = isPage(child) && isCountable(child) ? 1 : 0;
    const descendantContribution = countDescendants(
      child.id,
      parentToChildrenMap,
      isCountable
    );
    return count + childContribution + descendantContribution;
  }, 0);
};

/**
 * Returns all descendants of a node using pre-computed parent-to-children map.
 * This includes children, grandchildren, and all nested descendants recursively.
 */
export const getAllDescendants = (
  id: string,
  parentToChildrenMap: Map<string, PageRecordType[]>
): PageRecordType[] => {
  const children = parentToChildrenMap.get(id) || [];
  return children.concat(
    children.flatMap((child) =>
      getAllDescendants(child.id, parentToChildrenMap)
    )
  );
};

/**
 * Creates a flattened version of the PageNode data sorted in pre-order and
 * without any nodes that are children of a collapsed folder. This is likely
 * used to prep nodes for display in the PageList.
 *
 * @param  {PageState} pageState
 * @param  {PageManagerState} pageManagerState
 * @return {List.<ListNode>}
 */
const flattenedPageTree = (
  pageState: PageState,
  pageManagerState: PageManagerUIState,
  skipNestedInFolder: boolean = true,
  skipBranchPages: boolean = false,
  isCountable: (page: PageRecordType) => boolean = () => true
): List<PageListNodeType> => {
  const pageTree = getPageTreeNodes(pageState);

  const parentToChildrenMap = buildParentToChildrenMap(pageTree);

  return (function recur(node: PageRecordType): List<PageListNodeType> {
    if (isPage(node)) {
      const page = intoStaticListPage(node, pageState, pageManagerState);
      const list = List.of(page);
      if (isBranchPage(page)) {
        return skipBranchPages ? List.of() : list;
      }
      const pageBranches = List(parentToChildrenMap.get(page.id) || []);
      return list.concat<PageListNodeType>(
        pageBranches.flatMap<number, PageListNodeType>(recur)
      );
    }

    const folder = intoListFolder(
      node,
      pageState,
      pageManagerState,
      parentToChildrenMap,
      isCountable
    );

    if (!isRoot(folder) && !folder.isExpanded && skipNestedInFolder) {
      return List.of(folder);
    } else {
      const children = withHomePageFirst(
        List(parentToChildrenMap.get(folder.id) || [])
      );
      return List.of(folder).concat(children.flatMap(recur));
    }
  })(getRootNode(pageTree)).rest();
};

/**
 * If the provided node list contains the home page, returns a copy with
 * the home page moved to the top. Otherwise, returns exactly the provided list.
 *
 * @param  {List.<ListNode>} nodes
 * @return {List.<ListNode>}
 */
const withHomePageFirst = (nodes: any) => {
  // @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
  const homeIndex = nodes.findIndex((node) => node.isHome);
  if (homeIndex === -1) {
    return nodes;
  }

  return List.of(nodes.get(homeIndex)).concat(nodes.splice(homeIndex, 1));
};

/**
 * List of allowed static Usys utility pages to be included in the PageList.
 * Defined in the leaf PageLinkUtils helper (which also owns the static
 * page-link eligibility predicate) and re-exported here so the PageManager
 * module graph stays the import path for picker callers.
 */
export {allowedStaticUsysPages} from './PageLinkUtils';

/**
 * Constructs a flattened version of the PageTree with all nodes prepped for
 * display in a List style. If a node is currently selected (or being created),
 * it will be inserted/updated in the resulting List.
 *
 * @param {PageStore} pageState
 * @param {PageManagerUIStore} pageManagerState
 * @param {Object} options
 * @param {boolean} options.skipUtilityPages - Don't include Utility Pages
 * @return {List<ListNode>}
 */
export const staticPageList = (
  pageState: PageState,
  pageManagerState: PageManagerUIState,
  currentLocale: Locale | null | undefined,
  canDraftPagesPerLocale: boolean,
  // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type '{ skipUtilityPages: boolean; skipNestedInFolder: boolean; includeUsysUtilityPages: boolean; skipBranchPages: boolean; }': skipUtilityPages, skipNestedInFolder, includeUsysUtilityPages, skipBranchPages
  {
    skipUtilityPages = false,
    skipNestedInFolder = true,
    includeUsysUtilityPages = false,
    skipBranchPages = false,
    skipStandaloneBranchPageParents = false,
    skipStaticTemplates = false,
  }: {
    skipUtilityPages: boolean;
    skipNestedInFolder: boolean;
    includeUsysUtilityPages: boolean;
    skipBranchPages: boolean;
    skipStandaloneBranchPageParents: boolean;
    skipStaticTemplates: boolean;
  } = {}
  // this returns `List<PageListNodeType>` but everywhere imports it and assumes it's a `List<PageRecordType>`.
  // Unfortunately changing the types for every import would be a huge amount of work.
  // If you need a type for this in the future please use `List<PageListNodeType>`.
): any => {
  const currentPageState = getCurrentPageState(pageState, pageManagerState);
  // Standalone branch parents are never rendered, so counting them would
  // overstate a collapsed folder.
  const isCountable = (page: PageRecordType) =>
    !isStandaloneBranchParentPage(page, {
      currentLocale,
      canDraftPagesPerLocale,
    });
  const flattened = flattenedPageTree(
    currentPageState,
    pageManagerState,
    skipNestedInFolder,
    skipBranchPages,
    isCountable
  );

  const pageListWithUtilitySkipping = skipUtilityPages
    ? flattened.filter((page) => {
        if (includeUsysUtilityPages && page.utilKey) {
          return (
            allowedStaticUsysPages.has(page.utilKey) || !isUtilityPage(page)
          );
        }
        return !isUtilityPage(page);
      })
    : flattened;

  const pageListWithStandaloneBranchPageParentSkipping =
    skipStandaloneBranchPageParents
      ? pageListWithUtilitySkipping.filter(
          (page: any) =>
            !isStandaloneBranchParentPage(page, {
              currentLocale,
              canDraftPagesPerLocale,
            })
        )
      : pageListWithUtilitySkipping;

  return skipStaticTemplates
    ? pageListWithStandaloneBranchPageParentSkipping.filter(
        (page: any) => !isStaticTemplatePage(page)
      )
    : pageListWithStandaloneBranchPageParentSkipping;
};

type PartitionedStaticPages = {
  staticPages: ListType<PageRecordType>;
  staticUtilityPages: ListType<PageRecordType>;
  staticTemplatePages: ListType<PageRecordType>;
};

/**
 * @param pageState - The current state of the pages.
 * @param pageManagerState - The UI state of the page manager.
 * @param skipNestedInFolder - If set to `true`, excludes pages that are nested within folders from the returned list.
 *                             This can be useful for displaying only top-level pages without nested structures.
 *                             Defaults to `true`.
 *
 * @returns Retrieves a partitioned list of static pages into three groups:
 * (1) Static pages
 * (2) Static pages that are utility pages
 * (3) Static pages that are templates
 */
export function getPartitionedStaticPages(
  pageState: PageState,
  pageManagerState: PageManagerUIState,
  currentLocale: Locale | null | undefined,
  canDraftPagesPerLocale: boolean,
  skipNestedInFolder: boolean = true
): PartitionedStaticPages {
  const allStaticPages: ListType<PageRecordType> = staticPageList(
    pageState,
    pageManagerState,
    currentLocale,
    canDraftPagesPerLocale,
    {
      skipUtilityPages: false,
      skipNestedInFolder,
      includeUsysUtilityPages: false,
      skipBranchPages: false,
      skipStandaloneBranchPageParents: false,
      skipStaticTemplates: false,
    }
  );

  // Split the list of static pages into two based on if it's a utility page
  const [staticUtilityPages, staticPages] = partition(
    allStaticPages.toArray(),
    isUtilityPage
  );

  const [staticTemplatePages, staticPagesWithoutTemplates] = partition(
    staticPages,
    isStaticTemplatePage
  );

  return {
    staticPages: List(staticPagesWithoutTemplates),
    staticUtilityPages: List(staticUtilityPages),
    staticTemplatePages: List(staticTemplatePages),
  };
}

/**
 * Constructs a specialized list of dynamic pages for rendering in the PageList.
 * @param  {PageStore} pageState
 * @param  {PageManagerUIStore} pageManagerState
 * @return {List<ListNode>}
 */
export const dynPageList = (
  pageState: PageState,
  pageManagerState: PageManagerUIState
): ImmutableMap<string, PageListNodeType> => {
  const currentPageState = getCurrentPageState(pageState, pageManagerState);

  return getDynPages(currentPageState).map((page) =>
    intoDynamicListPage(page, pageManagerState)
  );
};

/**
 * Like dynPageList but uses persisted page state only (no unsaved settings merge).
 * Use this when partitioning pages by shouldPublish so the list only updates after Save,
 * matching the Pages Panel behavior.
 */
export const dynPageListPersisted = (
  pageState: PageState,
  pageManagerState: PageManagerUIState
): ImmutableMap<string, PageListNodeType> =>
  getDynPages(pageState).map((page) =>
    intoDynamicListPage(page, pageManagerState)
  );

///
// Constructors and type conversions.
///

/**
 * Converts a PageNode representing a static Page into a ListNode.
 *
 * @param  {PageNode} node
 * @param  {PageStore} pageState
 * @param  {PageManagerUIStore} pageManagerState
 * @return {ListNode}
 */
const intoStaticListPage = (
  node: any,
  pageState: PageState,
  pageManagerState: PageManagerUIState
) =>
  PageListNode(node).merge({
    isSelected: isSelected(node.id, pageManagerState),
    treeDepth: getNodeTreeDepth(node.id, getPageTreeNodes(pageState)),
    type: 'Page',
  });

/**
 * Converts a PageNode representing a dynamic Page into a ListNode.
 *
 * @param  {PageNode} node
 * @param  {PageManagerState} pageManagerState
 * @return {ListNode}
 */
const intoDynamicListPage = (
  node: any,
  pageManagerState: PageManagerUIState
): PageListNodeType =>
  PageListNode(node).merge({
    isSelected: isSelected(node.id, pageManagerState),
    type: 'Page',
  });

/**
 * Converts a PageNode representing a Folder into a ListNode.
 *
 * @param  {PageNode} node
 * @param  {PageState} pageState
 * @param  {PageManagerState} pageManagerState
 * @param  {Map<string, PageRecordType[]>} parentToChildrenMap - Pre-computed map for O(1) child lookups
 * @return {ListNode}
 */
const intoListFolder = (
  node: any,
  pageState: PageState,
  pageManagerState: PageManagerUIState,
  parentToChildrenMap: Map<string, PageRecordType[]>,
  isCountable: (page: PageRecordType) => boolean
) => {
  const pageTree = getPageTreeNodes(pageState);

  return PageListNode(node).merge({
    isExpanded: isExpanded(node.id, pageManagerState),
    isSelected: isSelected(node.id, pageManagerState),
    treeDepth: getNodeTreeDepth(node.id, pageTree),
    childCount: countDescendants(node.id, parentToChildrenMap, isCountable),
    type: 'Folder',
  });
};

///
// Predicates.
///

// Is there currently an item selected in the Page settings pane?
export const hasSelectedItem = (pageManagerState: PageManagerUIState) =>
  getSelectedItem(pageManagerState).id != null;

// Is the node with the provided `id` currently selected?
export const isSelected = (id: string, pageManagerState: PageManagerUIState) =>
  getSelectedItem(pageManagerState).get('id') === id;

// Has the selected Page or Folder been modified since it was selected?
export const selectionHasChanged = (
  pageState: PageState,
  pageManagerState: PageManagerUIState,
  currentLocale: Locale | null | undefined,
  canDraftPagesPerLocale: boolean
) =>
  !selection(pageManagerState).equals(
    selectionSnapshot(
      pageState,
      pageManagerState,
      currentLocale,
      canDraftPagesPerLocale
    )
  );

// Is the folder with the provided `id` expanded?
const isExpanded = (id: any, pageManagerState: PageManagerUIState) =>
  get('expandedFolders', pageManagerState).has(id);

// Is the provided node in the last position of its parent's `children` list?
const isLastSibling = (node: any, pageTreeNodes: Map<string, PageRecordType>) =>
  // @ts-expect-error - TS2769 - No overload matches this call.
  node.equals(nodesWithParentId(node.parentId, pageTreeNodes).last());

export const getPagesForCollections = (
  collections: CollectionsType,
  pages: ImmutableMap<string, PageListNodeType>,
  reversed = false
): List<PageRecordType> =>
  // @ts-expect-error - TS2740 Type 'Iterable<string, unknown>' is missing the following properties from type 'Map<string, CollectionRecordType>': set, delete, remove, clear, and 18 more.
  collections
    .flatMap((collection) => {
      const collectionPages = pages.filter(
        (page) => page.dynCollection === collection._id
      );
      const collectionPage = collectionPages.find(
        (page) => !isBranchPage(page)
      );
      const collectionBranch = collectionPages.find((page) =>
        isBranchPage(page)
      );
      const collectionPageObj: {[key: string]: PageListNodeType} = {};
      if (collectionPage) {
        const updatedCollectinPage = collectionPage
          .set('hasBranch', Boolean(collectionBranch))
          .set('isEcommerce', collection.plugin === COMMERCE_PLUGIN_KEY);

        collectionPageObj[collectionPage.id] = updatedCollectinPage;
      }
      if (collectionBranch) {
        collectionPageObj[collectionBranch.id] = collectionBranch;
      }
      if (reversed) {
        return immMap(collectionPageObj).reverse();
      }
      return immMap(collectionPageObj);
    })
    .filterNot(isNil);

export const getPageDisplayName = (
  page: PageRecordType | PageListNodeType,
  currentLocale: Locale | null | undefined,
  canDraftPagesPerLocale: boolean,
  showPrimaryName?: boolean
) => {
  const pageName =
    showPrimaryName || !currentLocale || currentLocale.primary
      ? page.name
      : (page.localizedSettings?.getIn([currentLocale._id, 'title']) ??
        page.name);

  if (isStandaloneBranchPage(page)) {
    return '[Branch] ' + pageName;
  }
  if (isBranchPage(page)) {
    return 'Branch';
  }
  if (
    isDraftPage(page, {currentLocale, canDraftPagesPerLocale}) &&
    !isStaticTemplatePage(page)
  ) {
    return '[Draft] ' + pageName;
  }
  if (isStaticTemplatePage(page)) {
    return pageName + ' Template';
  }
  return pageName;
};

/**
 * Returns a dynamic page with id pageId from the PageState
 *
 * @param {PageState} pageState - The current page state
 * @param {string} pageId - Id of the page to retrieve
 * @return {PageRecordType | null} - The dynamic page or null if none is selected
 */
export const getDynamicPage = (
  pageState: PageState,
  pageId: string | null | undefined
): PageRecordType | null => {
  if (!pageId) return null;

  return pageState.dynamicPages.get(pageId) || null;
};
